import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  useSnackbar,
} from '@munifocus/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_SAVED_SEARCH_NAME,
  productivityEnabledFor,
  type FileFilters,
  type SavedSearch,
} from '@munifocus/contracts-gov';
import {
  useCreateSavedSearch,
  useProductivitySettings,
  useUpdateSavedSearch,
} from '../../lib/productivity-api';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { useOptionalScope } from '../../providers/ScopeProvider';

/**
 * Name the current filters as a smart list, or rename one already saved.
 *
 * Sharing makes it a team list at one municipality: everyone who can read files
 * there sees it, each narrowed to the files they could already reach. Offered
 * only where the municipality has the productivity tools switched on.
 */
export function SaveSearchDialog({
  open,
  onClose,
  filters,
  editing,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  filters: FileFilters;
  /** Renaming/re-sharing this one instead of creating. */
  editing?: SavedSearch | null;
  onSaved?: (search: SavedSearch) => void;
}) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const scope = useOptionalScope();
  const settings = useProductivitySettings();
  const create = useCreateSavedSearch();
  const update = useUpdateSavedSearch();

  const places = (scope?.scopes ?? []).filter(
    (s) => s.kind === 'local_government' && productivityEnabledFor(settings.data, s.id),
  );
  const defaultPlace =
    scope?.active?.kind === 'local_government' && places.some((p) => p.id === scope.active?.id)
      ? scope.active.id
      : (places[0]?.id ?? '');

  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [place, setPlace] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setShared(editing?.shared ?? false);
    setPlace(editing?.localGovernmentId ?? defaultPlace);
    setSubmitted(false);
    // Reset per open; the defaults are read at that moment.
  }, [open, editing?.id]);

  const nameError = submitted && !name.trim();
  const saving = create.isPending || update.isPending;

  const onSave = () => {
    setSubmitted(true);
    if (!name.trim()) return;
    if (shared && !place) return;
    const done = {
      onSuccess: (search: SavedSearch) => {
        snackbar.success(t('gov.smartLists.saved', { name: search.name }));
        onSaved?.(search);
        onClose();
      },
      onError: (err: unknown) =>
        snackbar.error(apiErrorMessage(err, t('gov.smartLists.saveError'))),
    };
    if (editing) {
      update.mutate(
        { id: editing.id, name: name.trim(), shared, localGovernmentId: shared ? place : null },
        done,
      );
    } else {
      create.mutate(
        {
          name: name.trim(),
          filters: filters as Record<string, unknown>,
          shared,
          localGovernmentId: shared ? place : null,
        },
        done,
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {editing ? t('gov.smartLists.renameTitle') : t('gov.smartLists.saveTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            required
            label={t('gov.smartLists.name')}
            value={name}
            error={nameError}
            helperText={nameError ? t('common.validation.required') : undefined}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: MAX_SAVED_SEARCH_NAME } }}
          />
          {places.length > 0 ? (
            <FormControlLabel
              control={<Switch checked={shared} onChange={(e) => setShared(e.target.checked)} />}
              label={t('gov.smartLists.share')}
            />
          ) : null}
          {shared && places.length > 1 && (
            <TextField
              select
              fullWidth
              size="small"
              label={t('gov.smartLists.shareWith')}
              value={place}
              onChange={(e) => setPlace(e.target.value)}
            >
              {places.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {shared && (
            <Alert severity="info">
              {t('gov.smartLists.shareHint', {
                place: places.find((p) => p.id === place)?.name ?? '',
              })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.actions.cancel')}
        </Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? t('common.actions.saving') : t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
